import { useReduxState } from 'hooks';
import { getFormattedDate } from 'lib/common';
import {
  Avatar,
  Badge,
  Link,
  TextWithLineBreaks,
  TimeIndicator
} from 'components/common';
import { CoursePicture } from 'components/courses/common';

import type { CoursePicturePosition } from 'shared-types';
import type { BadgeCategory } from 'components/common/Badge';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  comment?: string;
  category?: BadgeCategory;
  date: string;
  icon: IconProp;
  picture?: string;
  picturePosition?: CoursePicturePosition;
  title: string;
  titleHref?: string;
  type: string;
}

const ProfileSummaryTimelineItem = ({
  comment,
  category = 'success-light',
  date,
  icon,
  picture,
  picturePosition,
  title,
  titleHref,
  type
}: Props) => {
  const { language, messages, theme } = useReduxState();

  return (
    <div className="item">
      <div className="item-icon">
        {picture ? (
          <div className="item-picture">
            <CoursePicture
              position={picturePosition}
              size={480}
              url={picture}
            />
          </div>
        ) : (
          <Avatar
            backgroundColor={theme.lightGrey}
            color={theme.textColor}
            icon={icon}
            fontSize="1.3rem"
            padding="6px 0"
            size="28px"
          />
        )}
      </div>
      <div className="item-content">
        <div className="item-title">
          {titleHref ? (
            <Link
              href={titleHref}
              linkColor="inherit"
              style={{ display: 'inline-block' }}
            >
              <div className="item-title-text">{title}</div>
            </Link>
          ) : (
            <div className="item-title-text">{title}</div>
          )}
        </div>
        <div className="item-type">
          <span className="item-type-title">{type}</span>
        </div>
        {comment ? (
          <div aria-label={messages.commonComment} className="item-comment">
            <TextWithLineBreaks text={comment} />
          </div>
        ) : null}
      </div>

      <div className="item-time">
        <Badge
          category={category}
          text={<TimeIndicator language={language} timeString={date} />}
          tooltip={getFormattedDate({ date })}
        />
      </div>

      <style jsx>{`
        .item {
          display: flex;
          gap: 15px;
          position: relative;
        }
        .item:not(:last-child)::before {
          background-color: ${theme.lightGrey};
          border-radius: 2px;
          content: '';
          position: absolute;
          left: 13px;
          top: 38px;
          bottom: -17px;
          width: 3px;
        }
        .item:not(:first-child) {
          margin-top: 30px;
        }
        .item-picture {
          border-radius: 6px;
          overflow: hidden;
          margin: 1px;
          position: relative;
          height: 26px;
          width: 26px;
        }
        .item-content {
          flex-grow: 1;
          padding: 3px;
        }
        .item-type {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: 600;
        }
        .item-type-title {
          margin-right: 7px;
        }
        .item-type-time {
          font-weight: 400;
        }
        .item-title {
          margin-bottom: 5px;
          width: 100%;
        }
        .item-title-text {
          color: ${theme.textColor};
          font-size: 1.6rem;
          font-weight: 700;
        }
        .item-comment {
          background-color: ${theme.lightGreyLighter};
          border-radius: 7px;
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          font-weight: 400;
          margin-top: 10px;
          padding: 10px;
          position: relative;
          top: -2px;
        }
        .item-time {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          font-weight: 600;
          padding: 4px 5px;
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
};

export default ProfileSummaryTimelineItem;
